import { EventMeta } from '../../../interfaces/meta.interface';

type SiteCollectionAdminAddedFields = {
    ObjectId: string;
};

const SiteCollectionAdminAdded: EventMeta<SiteCollectionAdminAddedFields> = {
    title: 'SharePoint Site Collection Admin Added',
    default: {
        description: 'SharePoint Site Collection Admin Added',
        longDescription:
            'A person was added as a site collection administrator. Site collection administrators have full permissions for the site collection and all subsites.',
        severity: 'info',
    },
    fieldsExtractor: (log: any) => {
        return {
            ObjectId: log.ObjectId,
        };
    },
    classifications: [
        // site admin added: fraudulent IP. not in allowed list.
        {
            severity: 'danger',
            description: (fields) => `A suspicious IP added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a OneDrive site collection administrator from a suspicious IP address that is not in your allowed list. This activity may indicate an attempt to gain unauthorized administrative access.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // site admin added: fraudulent IP. allowed IP.
        {
            severity: 'warning',
            description: (fields) =>
                `A suspicious IP within your allowed IP list added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on OneDrive. The action originated from a suspicious IP included in your allowed IP list. Although the IP range itself is normally trusted, the specific IP has been flagged as suspicious, making the change unusual. Review this administrative role assignment to confirm it was expected, as site collection administrator rights grant broad access and control.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedIP: true,
                isMicrosoftActivity: false,
            },
        },
        // site admin added: fraudulent IP. allowed region.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed region added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on OneDrive. The action originated from a suspicious IP located in your allowed region. Although the geographic location is normally considered trusted, the IP itself has been flagged as suspicious, making the activity unusual. Review this role assignment carefully, since site collection administrator rights provide broad control over OneDrive data and configuration.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedRegion: true,
                isMicrosoftActivity: false,
            },
        },
        // site admin added: fraudulent IP. in allowed countries
        {
            severity: 'warning',
            description: (fields) =>
                `A suspicious IP within your allowed countries added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on OneDrive. The action originated from a suspicious IP located in one of your allowed countries. Although the geographic location matches your access policies, the IP address has been flagged as suspicious, which makes the activity unusual. This role assignment should be reviewed carefully, since site collection administrator rights grant broad permissions and control over OneDrive data.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedCountry: true,
                isMicrosoftActivity: false,
            },
        },
        // site admin added: fraudulent IP. in allowed asn
        {
            severity: 'warning',
            description: (fields) =>
                `A suspicious IP within your allowed ASN list added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on OneDrive. The action originated from a suspicious IP included in your allowed ASN list. While the ASN itself is trusted, the specific IP has been flagged as suspicious. This makes the change unusual and worth attention, as site collection administrator rights grant broad permissions and control over OneDrive data and settings.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedASN: true,
                isMicrosoftActivity: false,
            },
        },
        // site admin added: not in allowed list.
        {
            severity: 'warning',
            description: (fields) => `An unapproved IP added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on OneDrive. The action originated from an unapproved IP outside your allowed list. Since site collection administrator rights grant broad access and control over OneDrive data, this change should be reviewed to confirm whether it was expected or potentially unauthorized.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
            },
        },
        // site admin added: microsoft activity
        {
            severity: 'info',
            description: (fields) =>
                `A Microsoft system operation (synchronization server IP) added a site collection administrator`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on OneDrive. This was logged as a Microsoft system operation event, with the IP address tied to the server managing synchronization. This entry reflects expected background activity. No action is required unless such changes appear inconsistent with your configuration or expected usage patterns.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        // site admin added
        {
            severity: 'info',
            description: (fields) => `A site collection administrator was added on SharePoint`,
            longDescription: (fields) =>
                `A person was added as a site collection administrator on OneDrive. This is an informational record of an administrative role change. No unusual conditions were associated with this entry. No action is required unless the assignment of site collection administrator rights was unexpected or coincides with other unusual activity.`,
        },
    ],
};

export default SiteCollectionAdminAdded;
